import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Button, 
  H3, 
  Intent, 
  Tag, 
  Callout,
  Icon,
  Spinner
} from '@blueprintjs/core';
import { getEloStatistics } from '../firebase/eloRecalculator';
import { subscribeToEloRatings, migrateEloDataFormat, getAllPlayerStatuses, setPlayerStatus, subscribeToPlayerStatuses } from '../firebase/eloManagement';
import { subscribeToActHistory } from '../firebase/actService';

function EloAdminPanel() {
  const [isMigrating, setIsMigrating] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [currentRatings, setCurrentRatings] = useState({});
  const [migrationResult, setMigrationResult] = useState(null);
  const [playerStatuses, setPlayerStatuses] = useState({});
  const [expandedPlayerManagement, setExpandedPlayerManagement] = useState(false);
  const [actHistory, setActHistory] = useState([]);

  useEffect(() => {
    // Load initial statistics
    loadStatistics();

    // Subscribe to real-time ELO updates
    const unsubscribeElo = subscribeToEloRatings((ratings) => {
      setCurrentRatings(ratings);
    });
    
    // Subscribe to player status updates
    const unsubscribeStatuses = subscribeToPlayerStatuses((statuses) => {
      setPlayerStatuses(statuses);
    });
    
    // Load initial player statuses
    getAllPlayerStatuses().then(statuses => {
      setPlayerStatuses(statuses);
    });
    
    // Subscribe to ACT history to get all players
    const unsubscribeHistory = subscribeToActHistory((history) => {
      setActHistory(history);
    });

    return () => {
      if (unsubscribeElo) {
        unsubscribeElo();
      }
      if (unsubscribeStatuses) {
        unsubscribeStatuses();
      }
      if (unsubscribeHistory) {
        unsubscribeHistory();
      }
    };
  }, []);

  const loadStatistics = async () => {
    try {
      const stats = await getEloStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading ELO statistics:', error);
    }
  };


  const handleMigration = async () => {
    setIsMigrating(true);
    setMigrationResult(null);

    try {
      console.log('Starting ELO data migration...');
      const result = await migrateEloDataFormat();
      
      setMigrationResult(result);

      // Reload statistics after migration
      if (result.success) {
        await loadStatistics();
      }
      
      console.log('ELO data migration complete!', result);
    } catch (error) {
      console.error('Error migrating ELO data:', error);
      setMigrationResult({
        success: false,
        message: `Migration failed: ${error.message}`
      });
    } finally {
      setIsMigrating(false);
    }
  };


  return (
    <Card style={{ padding: '1.5rem', marginBottom: '2rem' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '1.5rem'
      }}>
        <H3 style={{ 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem' 
        }}>
          <Icon icon="wrench" size={20} />
          ELO System Admin
          <Tag intent={Intent.WARNING} minimal>Admin Only</Tag>
        </H3>
      </div>

      {/* Current Statistics */}
      {statistics && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            padding: '1rem',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
              {statistics.totalPlayers}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Total Players
            </div>
          </div>
          
          <div style={{ 
            padding: '1rem',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-secondary)' }}>
              {statistics.averageElo}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Average ELO
            </div>
          </div>

          <div style={{ 
            padding: '1rem',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
              {statistics.highestElo.rating}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Highest ({statistics.highestElo.player || 'N/A'})
            </div>
          </div>

          <div style={{ 
            padding: '1rem',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>
              {statistics.lowestElo.rating < Infinity ? statistics.lowestElo.rating : 'N/A'}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Lowest ({statistics.lowestElo.player || 'N/A'})
            </div>
          </div>
        </div>
      )}

      {/* Migration Result */}
      {migrationResult && (
        <Callout 
          intent={migrationResult.success ? Intent.SUCCESS : Intent.DANGER}
          style={{ marginBottom: '1rem' }}
        >
          <div>
            <strong>{migrationResult.success ? 'Migration Complete!' : 'Migration Failed'}</strong>
            <div style={{ marginTop: '0.5rem' }}>{migrationResult.message}</div>
            {migrationResult.success && migrationResult.data && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                Player IDs: {Object.keys(migrationResult.data).join(', ')}
              </div>
            )}
          </div>
        </Callout>
      )}


      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Button
          intent={Intent.WARNING}
          icon="exchange"
          onClick={handleMigration}
          disabled={isMigrating}
          loading={isMigrating}
        >
          Migrate ELO Data Format
        </Button>


        <Button
          intent={Intent.NONE}
          icon="chart"
          onClick={loadStatistics}
          disabled={isMigrating}
        >
          Refresh Statistics
        </Button>
        
        <Button
          intent={Intent.PRIMARY}
          icon={expandedPlayerManagement ? "chevron-up" : "chevron-down"}
          onClick={() => setExpandedPlayerManagement(!expandedPlayerManagement)}
        >
          {expandedPlayerManagement ? 'Hide' : 'Show'} Player Management
        </Button>
      </div>
      
      {/* Player Status Management */}
      {expandedPlayerManagement && (
        <Card style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)' }}>
          <H3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>Player Status Management</H3>
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Player</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>ELO</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>ACTs Played</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Get all unique players from ACT history with case-insensitive normalization
                  const allPlayers = new Map();
                  const playerNameMapping = new Map(); // Maps normalized names to display names
                  
                  // Add all players from ACT history
                  actHistory.forEach(act => {
                    if (act.teams) {
                      Object.entries(act.teams).forEach(([team, teamData]) => {
                        if (teamData.players) {
                          Object.entries(teamData.players).forEach(([position, playerName]) => {
                            if (playerName && playerName !== `Player ${position}`) {
                              // Normalize the player name for comparison (lowercase, trimmed)
                              const normalizedName = playerName.toLowerCase().trim();
                              
                              if (!allPlayers.has(normalizedName)) {
                                // Store the first occurrence of the name as the display name
                                playerNameMapping.set(normalizedName, playerName);
                                
                                // Check for ELO under various name formats
                                const elo = currentRatings[playerName] || 
                                          currentRatings[normalizedName] ||
                                          currentRatings[playerName.trim()] ||
                                          null;
                                
                                allPlayers.set(normalizedName, { 
                                  name: playerName, // Use original casing for display
                                  normalizedName: normalizedName,
                                  actCount: 0,
                                  elo: elo
                                });
                              } else {
                                // Update to use the most common/recent casing
                                const existing = allPlayers.get(normalizedName);
                                // Keep the existing display name unless this one looks better
                                if (playerName.trim().length > 0 && !playerName.includes('Player')) {
                                  existing.name = playerName;
                                }
                              }
                              allPlayers.get(normalizedName).actCount++;
                            }
                          });
                        }
                      });
                    }
                  });
                  
                  // Sort players by ELO (with null values at the end), then by name
                  const sortedPlayers = Array.from(allPlayers.values()).sort((a, b) => {
                    if (a.elo === null && b.elo === null) return a.name.localeCompare(b.name);
                    if (a.elo === null) return 1;
                    if (b.elo === null) return -1;
                    return b.elo - a.elo;
                  });
                  
                  return sortedPlayers.map(player => {
                    const status = playerStatuses[player.name] || 'active';
                    return (
                      <tr key={player.name} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.5rem' }}>{player.name}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'center', fontWeight: 600 }}>
                          {player.elo !== null ? player.elo : 'N/A'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {player.actCount}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <Tag 
                            intent={status === 'active' ? Intent.SUCCESS : Intent.WARNING}
                            minimal
                          >
                            {status === 'active' ? 'Active' : 'Retired'}
                          </Tag>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <Button
                            small
                            intent={status === 'active' ? Intent.WARNING : Intent.SUCCESS}
                            onClick={async () => {
                              const newStatus = status === 'active' ? 'retired' : 'active';
                              await setPlayerStatus(player.name, newStatus);
                              // Update local state
                              setPlayerStatuses(prev => ({
                                ...prev,
                                [player.name]: newStatus
                              }));
                            }}
                          >
                            Mark as {status === 'active' ? 'Retired' : 'Active'}
                          </Button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Total Players: {(() => {
              const uniquePlayers = new Set();
              actHistory.forEach(act => {
                if (act.teams) {
                  Object.entries(act.teams).forEach(([team, teamData]) => {
                    if (teamData.players) {
                      Object.entries(teamData.players).forEach(([position, playerName]) => {
                        if (playerName && playerName !== `Player ${position}`) {
                          // Use normalized name to count unique players
                          const normalizedName = playerName.toLowerCase().trim();
                          uniquePlayers.add(normalizedName);
                        }
                      });
                    }
                  });
                }
              });
              const activeCount = uniquePlayers.size - Object.values(playerStatuses).filter(s => s === 'retired').length;
              const retiredCount = Object.values(playerStatuses).filter(s => s === 'retired').length;
              return uniquePlayers.size;
            })()} | 
            Active: {(() => {
              const uniquePlayers = new Set();
              actHistory.forEach(act => {
                if (act.teams) {
                  Object.entries(act.teams).forEach(([team, teamData]) => {
                    if (teamData.players) {
                      Object.entries(teamData.players).forEach(([position, playerName]) => {
                        if (playerName && playerName !== `Player ${position}`) {
                          const normalizedName = playerName.toLowerCase().trim();
                          uniquePlayers.add(normalizedName);
                        }
                      });
                    }
                  });
                }
              });
              return uniquePlayers.size - Object.values(playerStatuses).filter(s => s === 'retired').length;
            })()} | 
            Retired: {Object.values(playerStatuses).filter(s => s === 'retired').length}
          </div>
        </Card>
      )}

      {/* Info Callout */}
      <Callout intent={Intent.PRIMARY} icon="info-sign" style={{ marginTop: '1.5rem' }}>
        <strong>ELO System Admin Panel</strong>
        <div style={{ marginTop: '0.5rem' }}>
          This admin panel provides safe management tools for the ELO rating system:
        </div>
        <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
          <li><strong>Statistics:</strong> View current ELO distribution and player counts</li>
          <li><strong>Data Migration:</strong> Convert old database formats when needed</li>
          <li><strong>Real-time Monitoring:</strong> Track ELO ratings as they update automatically</li>
        </ul>
        <div style={{ marginTop: '0.5rem' }}>
          <strong>Important:</strong> ELO ratings are automatically updated when new ACT scorecards 
          are submitted. Manual intervention is rarely needed for normal operation.
        </div>
      </Callout>

    </Card>
  );
}

export default EloAdminPanel;